// 🔧 AdminInventoryPage.jsx
import React, { useEffect, useState } from "react";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import logUserAction from "./utils/logUserAction";
import * as images from "./images";

const AdminInventoryPage = ({ user }) => {
  const [inventory, setInventory] = useState({});
  const [selectedStore, setSelectedStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showStockEditor, setShowStockEditor] = useState(true);
  const [showTransferSection, setShowTransferSection] = useState(true);
  const [transferData, setTransferData] = useState({
    to: "",
    category: "Select",
    field: "",
    quantity: 0,
    search: ""
  });

  const fetchInventory = async () => {
    const snapshot = await getDocs(collection(db, "storeInventory"));
    const data = {};
    snapshot.forEach((doc) => {
      data[doc.id] = doc.data();
    });
    setInventory(data);
    setLastUpdated(new Date().toLocaleString());
    setLoading(false);

    try {
      console.log("Sending inventory to check-low-stock API...");
      await fetch("https://tifsu-ordering.onrender.com/api/check-low-stock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      console.log("✅ Request sent.");
    } catch (error) {
      console.error("⚠️ Failed to send low stock alert:", error);
    }
  };

  useEffect(() => {
    fetchInventory();
    const interval = setInterval(fetchInventory, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!user) return <div className="p-4">Loading user...</div>;

  const cookieImageMap = {
    Americano: images.Americano,
    Beurreberry: images.Beurreberry,
    Cappuccino: images.Cappuccino,
    Caramel: images.Caramel,
    ChaiLatte: images.ChaiLatte,
    Chipster: images.Chipster,
    ChocolatChaud: images.ChocolatChaud,
    Choconoix: images.Choconoix,
    DuoChoco: images.DuoChoco,
    Lemube: images.Lemube,
    Matcha: images.Matcha,
    Nougato: images.Nougato,
    Pistou: images.Pistou,
    Scarlette: images.Scarlette,
    Sucranelle: images.Sucranelle,
    CookieMonster: images.CookieMonster,
    Easter: images.Easter,
    Error: images.Error,
    Noisette: images.Noisette,
    Nuage: images.Nuage,
    Tofféo: images.Toffeo,
  };

  const productImageMap = {
    hotCups: images.hotDrinks,
    coldCups: images.coldDrinks,
    cookieBoxes: images.cookieBox,
  };

  const getCookieImage = (cookieName) => {
    const key = cookieName.replace(/\s+/g, "");
    return cookieImageMap[key] || "/broken.png";
  };

  const LOW_STOCK_THRESHOLDS = {
    cookies: 30,
    hotCups: 100,
    coldCups: 100,
    cookieBoxes: 50,
  };

  const saveChanges = async () => {
    const ref = doc(db, "storeInventory", selectedStore);
    await updateDoc(ref, inventory[selectedStore]);
    console.log("👤 Logging as user:", user?.email, user?.uid);
    await logUserAction(user, "Restocked Cookies", {
      store: selectedStore,
      cookies: inventory[selectedStore].cookies,
      other: {
        hotCups: inventory[selectedStore].hotCups,
        coldCups: inventory[selectedStore].coldCups,
        cookieBoxes: inventory[selectedStore].cookieBoxes
      }
    });    
    await logUserAction(user, "Updated Inventory", {
      store: selectedStore,
      data: inventory[selectedStore]
    });
    setModalMessage("✅ Inventory updated successfully.");
    setShowModal(true);
  };

  const handleTransfer = async () => {
    const { to, field, quantity, category } = transferData;
    const from = selectedStore;
    const qty = Number(quantity);
    if (!from || !to || from === to || qty <= 0) return setModalMessage("❌ Invalid transfer data.");
    if (!field) return setModalMessage("❌ Select an item to transfer.");
    if (category === "cookies" && inventory[from].cookies[field] < qty)
      return setModalMessage(`❌ Not enough ${field} in ${from} to transfer.`);
    if (category === "other" && inventory[from][field] < qty)
      return setModalMessage(`❌ Not enough ${field} in ${from} to transfer.`);

    const fromRef = doc(db, "storeInventory", from);
    const toRef = doc(db, "storeInventory", to);

    if (category === "cookies") {
      await updateDoc(fromRef, {
        ...inventory[from],
        cookies: {
          ...inventory[from].cookies,
          [field]: inventory[from].cookies[field] - qty
        }
      });

      await updateDoc(toRef, {
        ...inventory[to],
        cookies: {
          ...inventory[to].cookies,
          [field]: (inventory[to].cookies[field] || 0) + qty
        }
      });
    } else {
      await updateDoc(fromRef, {
        ...inventory[from],
        [field]: inventory[from][field] - qty
      });

      await updateDoc(toRef, {
        ...inventory[to],
        [field]: inventory[to][field] + qty
      });
    }
    
    await logUserAction(user, "Transferred Inventory", {
      from, to, category, field, quantity: qty
    });

    setModalMessage(`✅ ${qty} ${field} transferred from ${from} to ${to}.`);
    setShowModal(true);
    fetchInventory();
  };

  const store = inventory[selectedStore];

  const filteredFields = transferData.category === "cookies"
    ? Object.keys(store?.cookies || {})
    : ["hotCups", "coldCups", "cookieBoxes"];

  const filteredSearchResults = filteredFields.filter((field) =>
    field.toLowerCase().includes(transferData.search.toLowerCase())
  );

  if (loading) return <div className="p-4">Loading...</div>;

  const storeList = Object.keys(inventory);


  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-[#4c5d99] mb-4">Store Inventory Editor</h1>
      <p className="text-sm text-gray-600 mb-4">As of: {lastUpdated}</p>

      <select
        value={selectedStore}
        onChange={(e) => setSelectedStore(e.target.value)}
        className="mb-6 px-4 py-2 border rounded"
      >
        <option value="">Select a store</option>
        {storeList.map((storeName) => (
          <option key={storeName} value={storeName}>{storeName}</option>
        ))}
      </select>

      {selectedStore && store && (
        <>

          {/* Transfer UI */}
          <div className="mt-10 border-t pt-6">
            <button
              onClick={() => setShowTransferSection(!showTransferSection)}
              className="text-left font-bold text-xl text-[#4c5d99] mb-4 hover:underline"
            >
              {showTransferSection ? "▼" : "▶"} Transfer Inventory
            </button>

            {showTransferSection && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-2">From: <strong>{selectedStore}</strong></div>

                  <select
                    value={transferData.to}
                    onChange={(e) => setTransferData({ ...transferData, to: e.target.value })}
                    className="border px-3 py-2 rounded"
                  >
                    <option value="">To Store</option>
                    {storeList.filter(s => s !== selectedStore).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  <select
                    value={transferData.category}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setTransferData({ ...transferData, category: newValue, field: "", search: "" });
                    }}
                    className="border px-3 py-2 rounded"
                  >
                    <option value="Select" disabled>
                      Select an option
                    </option>
                    <option value="cookies">Cookies</option>
                    <option value="other">Other</option>
                  </select>

                  <input
                    type="number"
                    placeholder="Quantity"
                    value={transferData.quantity}
                    onChange={(e) => setTransferData({ ...transferData, quantity: e.target.value })}
                    className="border px-3 py-2 rounded"
                  />
                </div>

                {/* Search + Display */}
                <div className="mt-4">
                  {transferData.category !== "Select" && (
                    <>
                      <input
                        type="text"
                        placeholder="Search item..."
                        value={transferData.search}
                        onChange={(e) => setTransferData({ ...transferData, search: e.target.value })}
                        className="border px-3 py-2 rounded w-full md:w-1/2"
                      />
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                        {filteredSearchResults.map((field) => (
                          <div
                            key={field}
                            onClick={() => setTransferData({ ...transferData, field })}
                            className={`border rounded p-2 cursor-pointer flex items-center gap-2 hover:bg-green-200 ${transferData.field === field ? "bg-green-300" : ""}`}
                          >
                            <img
                              src={
                                transferData.category === "cookies"
                                  ? getCookieImage(field)
                                  : productImageMap[field] || images.CustomItem
                              }
                              alt={field}
                              className="w-10 h-10 object-contain"
                            />
                            <span className="text-sm font-medium truncate">{field}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <button
                  onClick={handleTransfer}
                  className="mt-6 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Transfer
                </button>
              </>
            )}
          </div>

          {/* 🍪 Cookie Stock Section */}
          <div className="mt-6">
            <button
              onClick={() => setShowStockEditor(!showStockEditor)}
              className="text-left font-bold text-xl text-[#4c5d99] mb-4 hover:underline"
            >
              {showStockEditor ? "▼" : "▶"} Edit Stock
            </button>

            {showStockEditor && (
              <>
                {/* Cookie Stock */}
                <h3 className="font-bold text-lg">Cookies</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
                  {Object.entries(store.cookies || {}).map(([cookie, value]) => (
                    <div key={cookie} className="flex items-center gap-2">
                      <div className={`p-1 rounded ${value < LOW_STOCK_THRESHOLDS.cookies ? 'bg-red-300' : ''}`}>
                        <img
                          src={getCookieImage(cookie)}
                          onError={(e) => { e.target.onerror = null; e.target.src = images.Error; }}
                          alt={cookie}
                          className="w-12 h-12 object-contain rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">{cookie}</label>
                        <input
                          type="number"
                          value={value}
                          onChange={(e) =>
                            setInventory((prev) => ({
                              ...prev,
                              [selectedStore]: {
                                ...prev[selectedStore],
                                cookies: {
                                  ...prev[selectedStore].cookies,
                                  [cookie]: Number(e.target.value),
                                },
                              },
                            }))
                          }
                          className="border px-2 py-1 rounded w-24"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Other Stock */}
                <h3 className="mt-6 font-bold text-lg">Other</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
                  {["hotCups", "coldCups", "cookieBoxes"].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <div className={`p-1 rounded ${store[item] < LOW_STOCK_THRESHOLDS[item] ? 'bg-red-300' : ''}`}>
                        <img
                          src={productImageMap[item] || images.CustomItem}
                          alt={item}
                          className="w-12 h-12 object-contain rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium capitalize">{item}</label>
                        <input
                          type="number"
                          value={store[item]}
                          onChange={(e) =>
                            setInventory((prev) => ({
                              ...prev,
                              [selectedStore]: {
                                ...prev[selectedStore],
                                [item]: Number(e.target.value),
                              },
                            }))
                          }
                          className="border px-2 py-1 rounded w-24"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Save & Restock Buttons */}
                <div className="mt-6 flex gap-2">
                  <button
                    onClick={saveChanges}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Save Changes
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )
      }

      {
        showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded shadow-lg text-center max-w-sm w-full">
              <p className="text-lg font-semibold text-black-600">{modalMessage}</p>
              <button
                onClick={() => setShowModal(false)}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default AdminInventoryPage;
